import { useState, useMemo, lazy, Suspense } from "react";
import {
  Calendar,
  MapPin,
  Pencil,
  Plane,
  Trash2,
} from "lucide-react";
import type { Account, DashboardData, Owner, Transaction, Trip, TripStatus } from "../../types";
import { dateLabel } from "../../lib/finance";
import { api } from "../../lib/api";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import Empty from "../../components/Empty";
import { usePrivacy } from "../../components/Privacy";
import SecondaryHeading from "../../app/SecondaryHeading";
import TransactionRow from "../../components/TransactionRow";
import { useTripStats } from "../../hooks/use-trip-stats";
import { inferCurrencyForDestination } from "../../lib/countries";
import TripWalletsSection from "./TripWalletsSection";
import TripBreakdownTabs from "./TripBreakdownTabs";
import TripHeroCard from "./TripHeroCard";
import TripActionsBar from "./TripActionsBar";

const TripConverter = lazy(() => import("./TripConverter"));
const TripForm = lazy(() => import("./TripForm"));
const TripRecapModal = lazy(() => import("./TripRecapModal"));

export interface TripDetailProps {
  trip: Trip;
  data: DashboardData;
  profile: Owner;
  onBack: () => void;
  onSaved: () => Promise<unknown> | void;
  onOpenTransaction: (t: Transaction) => void;
  onAddTransaction: (prefill: {
    tripId?: string;
    tripDestination?: string;
    type?: "Expense" | "Income" | "Transfer";
    accountId?: string;
    toAccountId?: string;
    amount?: string;
    exchangeRate?: string;
    notes?: string;
  }) => void;
}

export default function TripDetail({
  trip,
  data,
  profile,
  onBack,
  onSaved,
  onOpenTransaction,
  onAddTransaction,
}: TripDetailProps) {
  const [editing, setEditing] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showConverter, setShowConverter] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const stats = useTripStats(trip, data);
  const { hidden } = usePrivacy();

  const destinationCurrencies = (trip.destinations || [])
    .map((d) => inferCurrencyForDestination(d))
    .filter((c): c is string => Boolean(c));

  const tripCurrencies = [
    ...destinationCurrencies,
    ...stats?.walletBalances.map((w) => w.currency) || [],
    ...((stats?.tripTransactions || []).map((t) => t.currency)),
    trip.budgetCurrency || data.baseCurrency,
  ];

  async function updateStatus(newStatus: TripStatus) {
    setBusy(true);
    setError("");
    try {
      await api("v2/trips/save", {
        ...trip,
        status: newStatus,
      });
      await onSaved();
      if (newStatus === "completed") {
        setShowRecap(true);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function askFinish() {
    setConfirm({
      title: `Finish "${trip.name}"?`,
      message:
        "The trip will be marked completed and its recap will open. Nothing is deleted, and you can reopen it later from the trip page.",
      confirmLabel: "Finish trip",
      onConfirm: () => updateStatus("completed"),
    });
  }

  function askDelete() {
    setConfirm({
      title: `Delete trip "${trip.name}"?`,
      message: "The trip will be removed. Its transactions and accounts will stay in your household ledger.",
      confirmLabel: "Delete trip",
      onConfirm: async () => {
        setBusy(true);
        try {
          await api("v2/trips/delete", { id: trip.id });
          await onSaved();
          onBack();
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      },
    });
  }

  // Find leftover balances from past completed trips that can be rolled over into this trip
  const pastTripsWithLeftover = useMemo(() => {
    const list: {
      fromTrip: Trip;
      account: Account;
      currency: string;
      balance: number;
    }[] = [];
    for (const pt of data.trips || []) {
      if (pt.id === trip.id || pt.status !== "completed") continue;
      for (const aid of pt.accountIds || []) {
        const acc = (data.accounts || []).find((a) => a.id === aid);
        if (acc && acc.type.toLowerCase() === "cash" && acc.balance > 0) {
          const isRelevant =
            acc.currency === "USD" ||
            (stats?.walletBalances || []).some((wb) => wb.currency === acc.currency) ||
            trip.destinations.some(
              (d) => inferCurrencyForDestination(d) === acc.currency,
            );
          if (isRelevant) {
            list.push({
              fromTrip: pt,
              account: acc,
              currency: acc.currency,
              balance: acc.balance,
            });
          }
        }
      }
    }
    return list;
  }, [data.trips, data.accounts, trip, stats?.walletBalances]);

  async function handleRolloverFrom(item: {
    fromTrip: Trip;
    account: Account;
    currency: string;
    balance: number;
  }) {
    let targetWalletId = stats?.walletBalances.find(
      (wb) => wb.currency === item.currency && wb.account.type.toLowerCase() === "cash",
    )?.account.id;

    if (!targetWalletId) {
      setBusy(true);
      try {
        const walletName = `${trip.name} · ${item.currency} Cash`;
        const res = await api<{ account: Account }>("settings/accounts/save", {
          name: walletName,
          owner: profile,
          type: "Cash",
          currency: item.currency,
          openingBalance: "0",
          notes: `Cash wallet for trip: ${trip.name}`,
          active: true,
        });
        if (res?.account?.id) {
          targetWalletId = res.account.id;
          await onSaved();
        }
      } catch (e) {
        setError((e as Error).message);
        setBusy(false);
        return;
      } finally {
        setBusy(false);
      }
    }

    if (targetWalletId) {
      onAddTransaction({
        tripId: trip.id,
        tripDestination: trip.destinations[0],
        type: "Transfer",
        accountId: item.account.id,
        toAccountId: targetWalletId,
        amount: String(item.balance),
        exchangeRate: "1",
        notes: `Rollover from ${item.fromTrip.name}`,
      });
    }
  }

  if (!stats) return null;

  const {
    tripTransactions,
    budgetCurrency,
    budget,
    categoryBreakdown,
    destinationBreakdown,
    profileBreakdown,
    walletBalances,
  } = stats;

  return (
    <div className="trip-detail-screen">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />

      {editing && (
        <Suspense fallback={null}>
          <TripForm
            data={data}
            profile={profile}
            trip={trip}
            onDone={async () => {
              setEditing(false);
              await onSaved();
            }}
            onCancel={() => setEditing(false)}
          />
        </Suspense>
      )}

      {showRecap && (
        <Suspense fallback={null}>
          <TripRecapModal
            trip={trip}
            data={data}
            onClose={() => setShowRecap(false)}
            onAddTransaction={onAddTransaction}
          />
        </Suspense>
      )}

      <SecondaryHeading
        title={trip.name}
        icon={<Plane size={18} />}
        onBack={onBack}
      >
        <div className="trip-heading-actions">
          <button
            type="button"
            className="icon-button"
            onClick={() => setEditing(true)}
            aria-label="Edit trip"
            title="Edit trip"
          >
            <Pencil size={17} />
          </button>
          <button
            type="button"
            className="icon-button danger"
            onClick={askDelete}
            aria-label="Delete trip"
            title="Delete trip"
          >
            <Trash2 size={17} />
          </button>
        </div>
      </SecondaryHeading>

      {error && <div className="inline-error" role="alert">{error}</div>}

      {/* Meta Bar */}
      <div className="trip-meta-bar">
        <span className={`trip-status-badge ${trip.status}`}>{trip.status}</span>
        <span className="trip-date-pill">
          <Calendar size={13} />
          {dateLabel(trip.startDate)} – {dateLabel(trip.endDate)}
        </span>
        {(trip.destinations || []).map((d) => (
          <span key={d} className="destination-tag">
            <MapPin size={11} /> {d}
          </span>
        ))}
      </div>

      {/* Hero Spend & Pacing Card */}
      <TripHeroCard
        stats={stats}
        budget={budget}
        budgetCurrency={budgetCurrency}
        hidden={hidden}
        tripName={trip.name}
      />

      {/* Quick Action Bar */}
      <TripActionsBar
        trip={trip}
        data={data}
        busy={busy}
        showConverter={showConverter}
        onAddTransaction={onAddTransaction}
        onToggleConverter={() => setShowConverter(!showConverter)}
        onShowRecap={() => setShowRecap(true)}
        onUpdateStatus={(status) => (status === "completed" ? askFinish() : void updateStatus(status))}
      />

      {/* Converter Panel */}
      {showConverter && (
        <Suspense fallback={null}>
          <TripConverter
            baseCurrency={data.baseCurrency}
            tripCurrencies={tripCurrencies}
            destinations={trip.destinations}
            rates={data.rates}
          />
        </Suspense>
      )}

      {/* Cash Wallets Section */}
      <TripWalletsSection
        trip={trip}
        walletBalances={walletBalances}
        pastTripsWithLeftover={pastTripsWithLeftover}
        busy={busy}
        onRollover={handleRolloverFrom}
        onAddTransaction={onAddTransaction}
      />

      {/* Breakdowns & Insights Section */}
      <TripBreakdownTabs
        trip={trip}
        budgetCurrency={budgetCurrency}
        categoryBreakdown={categoryBreakdown}
        destinationBreakdown={destinationBreakdown}
        profileBreakdown={profileBreakdown}
      />

      {/* Filtered Transactions List */}
      <div className="trip-section">
        <div className="section-heading">
          <h3>Trip activity</h3>
          <span className="subtle-tag">{tripTransactions.length}</span>
        </div>

        {tripTransactions.length === 0 ? (
          <Empty
            title="No activity logged yet"
            description="Tap '+ Add expense', '+ Add income', or 'Transfer / FX' above to track activity for this trip."
          />
        ) : (
          <div className="trip-transactions-list">
            {tripTransactions.map((t) => (
              <TransactionRow
                key={t.id}
                t={t}
                accounts={data.accounts || []}
                payees={data.payees}
                trips={data.trips}
                hideTripBadge={trip.destinations.length <= 1}
                showDestinationOnly={trip.destinations.length > 1}
                onClick={() => onOpenTransaction(t)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
